import { useCallback, useEffect, useRef, useState } from "react";
import { ApiProblem } from "./httpClient";

/**
 * Các trạng thái của một tài nguyên tải qua API — tổng quát hoá state machine
 * của `ActorContext` (Sprint 2) cho mọi resource. S4.3/S4.4 consume trực tiếp
 * để dựng skeleton/empty/error/forbidden/stale (Spec S4.2 D3).
 */
export type ApiResourceState<T> =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly data: T; readonly fetchedAt: number }
  | { readonly status: "refreshing"; readonly data: T; readonly fetchedAt: number }
  | { readonly status: "stale"; readonly data: T; readonly fetchedAt: number; readonly requestId?: string }
  | { readonly status: "forbidden"; readonly requestId?: string }
  | { readonly status: "error"; readonly requestId?: string };

type HasData<T> = Extract<ApiResourceState<T>, { data: T }>;

function hasData<T>(state: ApiResourceState<T>): state is HasData<T> {
  return state.status === "ready" || state.status === "refreshing" || state.status === "stale";
}

/**
 * Hook điều phối lifecycle tải tài nguyên qua `loader` (bên gọi truyền
 * `apiRequest` — token tự lấy/refresh, không rò ra component; UI permission
 * là presentation-only, backend là authority — BR-SEC-01).
 *
 * Quy tắc ánh xạ lỗi (Spec D3):
 * - `ApiProblem{status:403}` → `forbidden` (kể cả khi đang có data cũ);
 * - lỗi còn lại khi ĐÃ có data → `stale` (giữ data cũ, kèm `requestId` nếu có);
 * - lỗi còn lại khi CHƯA có data → `error`;
 * - `requestId` lấy từ `ApiProblem.problem.requestId`;
 * - unmount/đổi deps abort request đang chạy; kết quả của request bị abort bị bỏ qua.
 */
export function useApiResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): { readonly state: ApiResourceState<T>; readonly refetch: () => void } {
  const [state, setState] = useState<ApiResourceState<T>>({ status: "loading" });
  const abortRef = useRef<AbortController | null>(null);
  // Giữ loader mới nhất mà không đưa nó vào deps (caller có thể truyền inline fn).
  const loaderRef = useRef(loader);
  useEffect(() => {
    loaderRef.current = loader;
  });

  const run = useCallback((mode: "initial" | "refresh") => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) =>
      mode === "refresh" && hasData(prev)
        ? { status: "refreshing", data: prev.data, fetchedAt: prev.fetchedAt }
        : { status: "loading" },
    );
    loaderRef.current(controller.signal).then(
      (data) => {
        if (controller.signal.aborted) return;
        setState({ status: "ready", data, fetchedAt: Date.now() });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        const requestId = error instanceof ApiProblem ? error.problem.requestId : undefined;
        // exactOptionalPropertyTypes: chỉ mang key requestId khi có giá trị.
        const idPart = requestId !== undefined ? { requestId } : {};
        setState((prev) => {
          if (error instanceof ApiProblem && error.status === 403) {
            return { status: "forbidden", ...idPart };
          }
          return hasData(prev)
            ? { status: "stale", data: prev.data, fetchedAt: prev.fetchedAt, ...idPart }
            : { status: "error", ...idPart };
        });
      },
    );
  }, []);

  useEffect(() => {
    run("initial");
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const refetch = useCallback(() => run("refresh"), [run]);

  return { state, refetch };
}
